import React, { useState, useEffect } from 'react';
import { tradingAPI } from '../../../../services/tradingAPI';
import '../../../../css/trading.css';

const Overview = () => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboard();
    const interval = setInterval(fetchDashboard, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await tradingAPI.getDashboard();
      setDashboard(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load dashboard');
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value || 0);
  };

  const formatPercent = (value) => {
    const num = parseFloat(value) || 0;
    const sign = num >= 0 ? '+' : '';
    return `${sign}${num.toFixed(2)}%`;
  };

  if (loading) {
    return <div className="loading">Loading your portfolio...</div>;
  }

  if (error) {
    return (
      <div className="error-message">
        <h3>Error Loading Dashboard</h3>
        <p>{error}</p>
        <button onClick={fetchDashboard} className="btn btn-primary">
          Retry
        </button>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="info-message">
        <h3>No Trading Account Found</h3>
        <p>Please contact support to set up your trading account.</p>
      </div>
    );
  }

  const {
    total_value,
    cash,
    positions_value,
    total_pl,
    total_pl_pct,
    daily_return,
    num_positions,
    top_positions,
    recent_orders,
    unread_alerts,
    alerts,
  } = dashboard;

  return (
    <div className="overview-tab">
      {/* Portfolio Summary */}
      <div className="dashboard-summary">
        <div className="summary-card">
          <div className="card-label">Portfolio Value</div>
          <div className="card-value">{formatCurrency(total_value)}</div>
          <div className={`card-change ${total_pl >= 0 ? 'positive' : 'negative'}`}>
            {formatCurrency(total_pl)} ({formatPercent(total_pl_pct)})
          </div>
        </div>

        <div className="summary-card">
          <div className="card-label">Cash</div>
          <div className="card-value">{formatCurrency(cash)}</div>
        </div>

        <div className="summary-card">
          <div className="card-label">Positions Value</div>
          <div className="card-value">{formatCurrency(positions_value)}</div>
          <div className="card-subtitle">{num_positions} positions</div>
        </div>

        <div className="summary-card">
          <div className="card-label">Daily Return</div>
          <div className={`card-value ${daily_return >= 0 ? 'positive' : 'negative'}`}>
            {formatPercent(daily_return)}
          </div>
        </div>
      </div>

      {/* Alerts */}
      {unread_alerts > 0 && (
        <div className="alerts-section">
          <div className="section-header">
            <h2>
              Alerts <span className="badge">{unread_alerts}</span>
            </h2>
          </div>
          <div className="alerts-list">
            {alerts.slice(0, 3).map((alert) => (
              <div key={alert.id} className={`alert alert-${alert.alert_type}`}>
                <div className="alert-icon">
                  {alert.alert_type === 'success' && '✓'}
                  {alert.alert_type === 'warning' && '⚠'}
                  {alert.alert_type === 'error' && '✕'}
                  {alert.alert_type === 'info' && 'ℹ'}
                </div>
                <div className="alert-content">
                  <div className="alert-title">{alert.title}</div>
                  <div className="alert-message">{alert.message}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Positions */}
      <div className="positions-section">
        <div className="section-header">
          <h2>Top Positions</h2>
        </div>
        {top_positions && top_positions.length > 0 ? (
          <div className="positions-table">
            <table>
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Qty</th>
                  <th>Entry Price</th>
                  <th>Current Price</th>
                  <th>Market Value</th>
                  <th>P/L</th>
                  <th>P/L %</th>
                </tr>
              </thead>
              <tbody>
                {top_positions.map((position) => (
                  <tr key={position.id}>
                    <td className="symbol">{position.symbol}</td>
                    <td>{position.qty}</td>
                    <td>{formatCurrency(position.entry_price)}</td>
                    <td>{formatCurrency(position.current_price)}</td>
                    <td>{formatCurrency(position.market_value)}</td>
                    <td className={position.unrealized_pl >= 0 ? 'positive' : 'negative'}>
                      {formatCurrency(position.unrealized_pl)}
                    </td>
                    <td className={position.unrealized_pl_pct >= 0 ? 'positive' : 'negative'}>
                      {formatPercent(position.unrealized_pl_pct)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">No active positions</div>
        )}
      </div>

      {/* Recent Orders */}
      <div className="orders-section">
        <div className="section-header">
          <h2>Recent Orders</h2>
        </div>
        {recent_orders && recent_orders.length > 0 ? (
          <div className="orders-table">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Symbol</th>
                  <th>Side</th>
                  <th>Type</th>
                  <th>Qty</th>
                  <th>Price</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recent_orders.slice(0, 5).map((order) => (
                  <tr key={order.id}>
                    <td>{new Date(order.created_at).toLocaleDateString()}</td>
                    <td className="symbol">{order.symbol}</td>
                    <td>
                      <span className={`badge badge-${order.side}`}>{order.side.toUpperCase()}</span>
                    </td>
                    <td>{order.order_type}</td>
                    <td>{order.qty}</td>
                    <td>{formatCurrency(order.limit_price || order.filled_price)}</td>
                    <td>
                      <span className={`badge badge-${order.status}`}>{order.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">No recent orders</div>
        )}
      </div>
    </div>
  );
};

export default Overview;
